import React, { useEffect, useState } from "react";
import {
    Alert,
    Avatar,
    Box,
    Button,
    Card,
    CardContent,
    CardHeader,
    Chip,
    CircularProgress,
    Divider,
    FormControl,
    IconButton,
    InputLabel,
    Link,
    MenuItem,
    OutlinedInput,
    Select,
    InputAdornment,
    Stack,
    Typography,
} from "@mui/material";
import DeleteIcon from "@mui/icons-material/Delete";
import AddIcon from "@mui/icons-material/Add";
import { useParams } from "react-router-dom";
import api from "../../utils/api";

const LANGUAGE_LABELS = {
    en: "English",
    ru: "Russian",
    hy: "Armenian",
};

const CURRENCY_LABELS = {
    USD: "US Dollar (USD)",
    EUR: "Euro (EUR)",
    GBP: "British Pound (GBP)",
    AMD: "Armenian Dram (AMD)",
    RUB: "Russian Ruble (RUB)",
};

function userInitial(user) {
    const first = (user?.first_name || "").trim()[0];
    const last = (user?.last_name || "").trim()[0];
    return (first || last || "?").toUpperCase();
}

function formatDateTime(value) {
    if (!value) return "—";
    const date = new Date(value);
    return Number.isNaN(date.getTime()) ? value : date.toLocaleString();
}

function formatDate(value) {
    if (!value) return "—";
    const date = new Date(value);
    return Number.isNaN(date.getTime()) ? value : date.toLocaleDateString();
}

function formatGender(value) {
    if (!value) return "—";
    if (value === "male") return "Male";
    if (value === "female") return "Female";
    return value;
}

function DetailRow({ label, value }) {
    return (
        <Box>
            <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 0.25 }}>
                {label}
            </Typography>
            <Typography variant="body2" sx={{ fontWeight: 500, wordBreak: "break-word" }}>
                {value || "—"}
            </Typography>
        </Box>
    );
}

function EmptyState({ children }) {
    return (
        <Box sx={{ py: 5, px: 3, textAlign: "center" }}>
            <Typography variant="body2" color="text.secondary">
                {children}
            </Typography>
        </Box>
    );
}

export default function TeacherProfile() {
    const { id } = useParams();
    const [loading, setLoading] = useState(true);
    const [data, setData] = useState(null);
    const [rates, setRates] = useState([]);
    const [lessonTypes, setLessonTypes] = useState([]);
    const [ratesLoading, setRatesLoading] = useState(true);
    const [ratesSaving, setRatesSaving] = useState(false);
    const [ratesError, setRatesError] = useState("");
    const [ratesSuccess, setRatesSuccess] = useState("");

    useEffect(() => {
        let mounted = true;
        (async () => {
            setLoading(true);
            try {
                const [profileRes, ratesRes, typesRes] = await Promise.all([
                    api.get(`admin/teachers/${id}/profile`),
                    api.get(`admin/teachers/${id}/rates`),
                    api.get("admin/lesson-types/list"),
                ]);
                if (mounted) {
                    setData(profileRes.data?.data ?? null);
                    setRates((ratesRes.data?.data ?? []).map((row) => ({
                        lesson_type_id: String(row.lesson_type_id),
                        hourly_rate: String(row.hourly_rate ?? ""),
                        currency: "AMD",
                    })));
                    setLessonTypes(typesRes.data?.data ?? []);
                }
            } catch {
                if (mounted) {
                    setData(null);
                    setRates([]);
                    setLessonTypes([]);
                    setRatesError("Failed to load teacher rates.");
                }
            } finally {
                if (mounted) {
                    setLoading(false);
                    setRatesLoading(false);
                }
            }
        })();
        return () => {
            mounted = false;
        };
    }, [id]);

    const addRateRow = () => {
        setRates((prev) => [...prev, { lesson_type_id: "", hourly_rate: "", currency: "AMD" }]);
    };

    const updateRateRow = (index, field, value) => {
        setRates((prev) => prev.map((row, i) => (i === index ? { ...row, [field]: value } : row)));
    };

    const removeRateRow = (index) => {
        setRates((prev) => prev.filter((_, i) => i !== index));
    };

    const saveRates = async () => {
        setRatesSaving(true);
        setRatesError("");
        setRatesSuccess("");
        try {
            const payload = {
                rates: rates
                    .filter((row) => row.lesson_type_id && row.hourly_rate !== "")
                    .map((row) => ({
                        lesson_type_id: Number(row.lesson_type_id),
                        hourly_rate: Number(row.hourly_rate),
                        currency: "AMD",
                    })),
            };
            await api.put(`admin/teachers/${id}/rates`, payload);
            setRatesSuccess("Rates saved successfully.");
        } catch (e) {
            const validationErrors = e.errors || e.response?.data?.errors;
            const msg =
                validationErrors?.rates?.[0] ||
                (validationErrors ? Object.values(validationErrors)[0]?.[0] : null) ||
                e.response?.data?.message ||
                e.message ||
                "Failed to save rates.";
            setRatesError(msg || "Failed to save rates.");
        } finally {
            setRatesSaving(false);
        }
    };

    if (loading) {
        return (
            <Box sx={{ pt: 3, px: { xs: 2, sm: 3 }, pb: 4 }}>
                <CircularProgress size={24} />
                <Typography sx={{ mt: 1 }}>Loading teacher profile...</Typography>
            </Box>
        );
    }

    if (!data?.teacher) {
        return (
            <Box sx={{ pt: 3, px: { xs: 2, sm: 3 }, pb: 4 }}>
                <Typography>Teacher profile not found.</Typography>
            </Box>
        );
    }

    const teacher = data.teacher;
    const fullName = `${teacher.first_name || ""} ${teacher.last_name || ""}`.trim();
    const countryDisplay = teacher.country_name || teacher.country || "—";
    const languageDisplay = LANGUAGE_LABELS[teacher.language] || teacher.language || "—";
    const currencyDisplay = CURRENCY_LABELS[teacher.currency] || teacher.currency || "—";

    return (
        <Box sx={{ pt: 3, pb: 4, px: { xs: 2, sm: 3 }, maxWidth: 1600, mx: "auto" }}>
            <Typography component="h1" variant="h5" sx={{ mb: 3, fontWeight: 600 }}>
                Teacher Profile
            </Typography>

            <Box sx={{ display: "grid", gap: 2.5 }}>
                <Card variant="outlined">
                    <CardHeader title="Profile Info" sx={{ pb: 0 }} />
                    <Divider />
                    <CardContent sx={{ pt: 2.5 }}>
                        <Box sx={{ display: "flex", gap: 2.5, alignItems: "flex-start", mb: 3 }}>
                            {teacher.avatar ? (
                                <Avatar src={teacher.avatar} alt={fullName || "Teacher"} sx={{ width: 88, height: 88 }} />
                            ) : (
                                <Avatar sx={{ width: 88, height: 88, fontSize: "1.75rem", fontWeight: 600 }}>
                                    {userInitial(teacher)}
                                </Avatar>
                            )}
                            <Box sx={{ minWidth: 0, flex: 1 }}>
                                <Typography variant="h6" sx={{ fontWeight: 600 }}>
                                    {fullName || `Teacher #${teacher.id}`}
                                </Typography>
                                <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                                    {teacher.email || "—"}
                                </Typography>
                                <Stack direction="row" spacing={1} sx={{ mt: 1.5 }} flexWrap="wrap" useFlexGap>
                                    <Chip
                                        size="small"
                                        label={teacher.email_verified_at ? "Email verified" : "Email not verified"}
                                        color={teacher.email_verified_at ? "success" : "default"}
                                        variant="outlined"
                                    />
                                    <Chip
                                        size="small"
                                        label={teacher.phone_verified_at ? "Phone verified" : "Phone not verified"}
                                        color={teacher.phone_verified_at ? "success" : "default"}
                                        variant="outlined"
                                    />
                                </Stack>
                            </Box>
                        </Box>

                        <Typography variant="subtitle2" sx={{ mb: 1.5, fontWeight: 600 }}>
                            Personal
                        </Typography>
                        <Box
                            sx={{
                                display: "grid",
                                gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr", md: "1fr 1fr 1fr" },
                                gap: 2,
                                mb: 3,
                            }}
                        >
                            <DetailRow label="First name" value={teacher.first_name} />
                            <DetailRow label="Last name" value={teacher.last_name} />
                            <DetailRow label="Phone" value={teacher.phone} />
                            <DetailRow label="Position" value={teacher.position} />
                            <DetailRow label="Gender" value={formatGender(teacher.gender)} />
                            <DetailRow label="Date of birth" value={formatDate(teacher.dob)} />
                            <DetailRow label="Language" value={languageDisplay} />
                        </Box>

                        <Typography variant="subtitle2" sx={{ mb: 1.5, fontWeight: 600 }}>
                            Location
                        </Typography>
                        <Box
                            sx={{
                                display: "grid",
                                gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr", md: "1fr 1fr 1fr" },
                                gap: 2,
                                mb: 3,
                            }}
                        >
                            <DetailRow label="Country" value={countryDisplay} />
                            <DetailRow label="City" value={teacher.city} />
                            <DetailRow label="State / Region" value={teacher.state} />
                            <DetailRow label="Address" value={teacher.address} />
                            <DetailRow label="ZIP / Postal code" value={teacher.zip} />
                        </Box>

                        <Typography variant="subtitle2" sx={{ mb: 1.5, fontWeight: 600 }}>
                            Preferences
                        </Typography>
                        <Box
                            sx={{
                                display: "grid",
                                gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr", md: "1fr 1fr 1fr" },
                                gap: 2,
                            }}
                        >
                            <DetailRow label="Timezone" value={teacher.timezone} />
                            <DetailRow label="Currency" value={currencyDisplay} />
                            <DetailRow label="Registered" value={formatDateTime(teacher.created_at)} />
                            <DetailRow label="Last updated" value={formatDateTime(teacher.updated_at)} />
                        </Box>
                    </CardContent>
                </Card>

                <Box
                    sx={{
                        display: "grid",
                        gridTemplateColumns: { xs: "1fr", md: "1fr 1fr" },
                        gap: 2.5,
                        alignItems: "start",
                    }}
                >
                <Card variant="outlined">
                    <CardHeader title="Classrooms" sx={{ pb: 0 }} />
                    <Divider />
                    <CardContent sx={{ p: 0 }}>
                        {!data.classrooms?.length ? (
                            <EmptyState>No classrooms linked.</EmptyState>
                        ) : (
                            <Stack spacing={0} divider={<Divider />}>
                                {data.classrooms.map((classroom) => (
                                    <Box
                                        key={classroom.id}
                                        sx={{
                                            display: "flex",
                                            justifyContent: "space-between",
                                            alignItems: "center",
                                            gap: 2,
                                            p: 2.5,
                                            flexWrap: "wrap",
                                        }}
                                    >
                                        <Box>
                                            <Typography sx={{ fontWeight: 600 }}>{classroom.name}</Typography>
                                            <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                                                {classroom.course_name || "—"} • {classroom.status || "—"} • Students:{" "}
                                                {classroom.students ?? 0} • Lessons: {classroom.lessons ?? 0}
                                            </Typography>
                                            <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 0.5 }}>
                                                {classroom.start_date || "—"} → {classroom.end_date || "—"}
                                            </Typography>
                                        </Box>
                                        <Link
                                            href={`/classrooms/${classroom.id}`}
                                            target="_blank"
                                            rel="noopener noreferrer"
                                            underline="hover"
                                        >
                                            Open classroom
                                        </Link>
                                    </Box>
                                ))}
                            </Stack>
                        )}
                    </CardContent>
                </Card>

                <Card variant="outlined">
                    <CardHeader
                        title="Rates"
                        sx={{ pb: 0 }}
                        action={
                            <Button variant="outlined" size="small" startIcon={<AddIcon />} onClick={addRateRow}>
                                Add rate
                            </Button>
                        }
                    />
                    <Divider />
                    <CardContent sx={{ pt: 2.5 }}>
                        {ratesLoading ? (
                            <CircularProgress size={20} />
                        ) : (
                            <Stack spacing={1.5}>
                                {ratesError ? <Alert severity="error">{ratesError}</Alert> : null}
                                {ratesSuccess ? <Alert severity="success">{ratesSuccess}</Alert> : null}
                                {!rates.length ? (
                                    <EmptyState>No rates configured yet.</EmptyState>
                                ) : null}
                                {rates.map((row, index) => (
                                    <Box
                                        key={`rate-${index}`}
                                        sx={{
                                            display: "grid",
                                            gridTemplateColumns: {
                                                xs: "1fr",
                                                md: "minmax(240px,1fr) minmax(160px,220px) 100px 40px",
                                            },
                                            gap: 1,
                                            alignItems: "center",
                                        }}
                                    >
                                        <FormControl size="small" fullWidth>
                                            <InputLabel id={`rate-lesson-type-${index}`}>Lesson type</InputLabel>
                                            <Select
                                                labelId={`rate-lesson-type-${index}`}
                                                label="Lesson type"
                                                value={row.lesson_type_id}
                                                onChange={(e) => updateRateRow(index, "lesson_type_id", e.target.value)}
                                            >
                                                <MenuItem value="">
                                                    <em>Select lesson type</em>
                                                </MenuItem>
                                                {lessonTypes.map((type) => (
                                                    <MenuItem key={type.id} value={String(type.id)}>
                                                        {type.name}
                                                    </MenuItem>
                                                ))}
                                            </Select>
                                        </FormControl>
                                        <OutlinedInput
                                            size="small"
                                            type="number"
                                            value={row.hourly_rate}
                                            onChange={(e) => updateRateRow(index, "hourly_rate", e.target.value)}
                                            inputProps={{ min: 0, step: 0.01 }}
                                            placeholder="Hourly rate"
                                            startAdornment={<InputAdornment position="start">֏</InputAdornment>}
                                        />
                                        <IconButton color="error" onClick={() => removeRateRow(index)}>
                                            <DeleteIcon />
                                        </IconButton>
                                    </Box>
                                ))}
                                {rates.length > 0 ? (
                                    <Box sx={{ display: "flex", justifyContent: "flex-end", pt: 1 }}>
                                        <Button variant="contained" onClick={saveRates} disabled={ratesSaving} sx={{ color: "#fff" }}>
                                            {ratesSaving ? "Saving..." : "Save rates"}
                                        </Button>
                                    </Box>
                                ) : null}
                            </Stack>
                        )}
                    </CardContent>
                </Card>
                </Box>
            </Box>
        </Box>
    );
}
